/**
 * Exercise Image Utilities
 * 
 * Provides helper functions for loading exercise animations with fallback support.
 * Prioritizes locally hosted WebP animations over external GIF URLs.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Get the best available animation URL for an exercise.
 * Priority: animation_webp (local) > gif_url > image_url
 * 
 * @param {Object} exercise - Exercise object from database or API
 * @returns {string|null} - The best available animation URL or null
 */
export const getExerciseAnimationUrl = (exercise) => {
  if (!exercise) return null;
  
  // Priority 1: Local WebP animation (optimized)
  if (exercise.animation_webp) {
    // If it's a relative path starting with /media, convert to /api/media
    if (exercise.animation_webp.startsWith('/media/')) {
      return `${API_URL}/api${exercise.animation_webp}`;
    }
    // If it starts with just /, prepend the API URL with /api
    if (exercise.animation_webp.startsWith('/')) {
      return `${API_URL}/api${exercise.animation_webp}`;
    }
    return exercise.animation_webp;
  }
  
  // Priority 2: gif_url field (if exists)
  if (exercise.gif_url) {
    return exercise.gif_url;
  }
  
  // Priority 3: image_url field (original external GIF)
  if (exercise.image_url) {
    return exercise.image_url;
  }
  
  return null;
};

/**
 * Get animation URL with library lookup fallback.
 * Prioritizes library data (which has animation_webp) over embedded exercise data.
 * Useful when exercise data stored in workouts might have old external URLs.
 * 
 * @param {Object} exercise - Exercise object (may be from workout with old data)
 * @param {Object} exerciseLibrary - Map of exercise_id -> exercise details (has updated animation_webp)
 * @returns {string|null} - The best available animation URL (preferring local WebP)
 */
export const getExerciseAnimationWithFallback = (exercise, exerciseLibrary = {}) => {
  if (!exercise) return null;
  
  // First try to find in library by ID - library has updated animation_webp
  const exerciseId = exercise.exercise_id || exercise.id;
  if (exerciseId && exerciseLibrary[exerciseId]) {
    const libraryUrl = getExerciseAnimationUrl(exerciseLibrary[exerciseId]);
    if (libraryUrl) return libraryUrl;
  }
  
  // Fallback to exercise object itself (for cases where library lookup fails)
  return getExerciseAnimationUrl(exercise);
};

export default {
  getExerciseAnimationUrl,
  getExerciseAnimationWithFallback
};
